import { useMemo, useRef, useState } from "react";
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from "react-native-reanimated";
import OpacityView from "src/components/OpacityView";
import { DefaultPalette } from "src/constants/DefaultPalette";
import isPaletteMatch from "src/helpers/themeHelpers/isPaletteMatch";
import IconDice from "src/icons/IconDice";
import {
	PaletteOption,
	ReactionOption,
	StickerPackOption,
	ThemeOptionItem,
} from "src/screens/(internal)/CustomThemeModal/types";
import { paddingSmall } from "src/styles/spacing";
import { AnimatePresence, Image } from "src/swsh-native";
import FloatingThemeOption from "./FloatingThemeOption";

type Option = {
	id: string;
	palette: NonNullable<PaletteOption>;
	stickerPack?: StickerPackOption;
	reaction?: ReactionOption;
};

const FloatingThemeShuffle = ({
	selectedPalette,
	selectedStickerPackId,
	extraSuggestions = [],
	onPress,
}: {
	selectedPalette: PaletteOption;
	selectedStickerPackId: string | null;
	extraSuggestions?: ThemeOptionItem[];
	onPress?: (
		suggestion: ThemeOptionItem & {
			isSelected: boolean;
			isShuffle?: boolean;
		},
	) => void;
}) => {
	const options = useMemo<Option[]>(() => {
		const filteredExtraSuggestions = extraSuggestions.filter((suggestion) =>
			Boolean(
				suggestion.palette &&
					suggestion.palette.primary &&
					suggestion.palette.secondary &&
					suggestion.palette.tertiary,
			),
		) as Omit<Option, "id">[];
		const shuffledSuggestions: Omit<Option, "id">[] = filteredExtraSuggestions.sort(
			() => Math.random() - 0.5,
		);
		const mappedSuggestions: Option[] = shuffledSuggestions.map((suggestion) => ({
			...suggestion,
			id:
				suggestion?.stickerPack?.stickerPackId ??
				`${suggestion.palette.primary}-${suggestion.palette.secondary}-${suggestion.palette.tertiary}`,
		}));
		return mappedSuggestions;
	}, [extraSuggestions]);
	const numShufflesRef = useRef(0);
	const [selectedId, setSelectedId] = useState<string | null>(options[0]?.id ?? null);

	const suggestion = useMemo<Option | undefined>(() => {
		if (!selectedId) return;
		return options.find((option) => option.id === selectedId);
	}, [options, selectedId]);
	const isSelected = useMemo(() => {
		if (!suggestion) return false;
		if (suggestion.stickerPack) {
			if (suggestion.stickerPack.stickerPackId === selectedStickerPackId) return true;
			return false;
		}
		return isPaletteMatch(selectedPalette, suggestion.palette);
	}, [selectedPalette, selectedStickerPackId, suggestion]);

	const rotationSV = useSharedValue(0);
	const animatedStyle = useAnimatedStyle(() => {
		return {
			transform: [{ rotate: `${rotationSV.value}deg` }],
		};
	}, [rotationSV]);
	const stickerThumbnailUrl = suggestion?.stickerPack?.thumbnailUrl;

	if (!suggestion) return null;
	return (
		<FloatingThemeOption
			suggestion={
				!isSelected
					? {
							// Hacky but makes it look different from default
							palette: {
								primary: DefaultPalette.tertiary,
								secondary: DefaultPalette.primary,
								tertiary: DefaultPalette.secondary,
							},
							isSelected: false,
						}
					: { ...suggestion, isSelected }
			}
			onPress={() => {
				numShufflesRef.current += 1;
				const curIndex = options.findIndex((option) => option.id === selectedId);
				const newOption = options[(curIndex + 1) % options.length];
				if (!newOption) return;
				setSelectedId(newOption.id);
				rotationSV.value = withSpring(numShufflesRef.current * 120);

				if (!newOption) return;
				onPress?.({
					palette: newOption.palette,
					reaction: newOption.reaction,
					stickerPack: newOption.stickerPack,
					isSelected: false,
					isShuffle: true,
				});
			}}
		>
			<Animated.View style={animatedStyle}>
				<IconDice size={20} />
			</Animated.View>
			<AnimatePresence>
				{!!stickerThumbnailUrl && (
					<OpacityView
						style={{
							position: "absolute",
							bottom: paddingSmall,
							right: paddingSmall,
						}}
					>
						<Image
							alt="Sticker"
							src={stickerThumbnailUrl}
							dim={{
								width: 16,
								height: 16,
							}}
							style={{
								transform: [{ rotate: "-10deg" }],
							}}
							contentFit="contain"
						/>
					</OpacityView>
				)}
			</AnimatePresence>
		</FloatingThemeOption>
	);
};
export default FloatingThemeShuffle;
